export type DurationUnit = 'd' | 'h' | 'm' | 's';
type DurationUnitParts = Record<DurationUnit, number>;
export type DurationPreset = { label: string; seconds: number };

export const ALL_DURATION_UNITS: readonly DurationUnit[] = ['d', 'h', 'm', 's'] as const;

export const UNIT_SECONDS: Record<DurationUnit, number> = {
  d: 86400,
  h: 3600,
  m: 60,
  s: 1
};

/**
 * Extract seconds into { d, h, m, s } parts for consumption by secondsToTimeString.
 * Used by the existing secondsToTimeString implementation.
 */
export const secondsToParts = ({ seconds, includeDays }: { seconds: number; includeDays: boolean }) => {
  const totalHours = Math.floor(seconds / 3600);
  const days = includeDays ? Math.floor(totalHours / 24) : 0;
  const hours = includeDays ? totalHours % 24 : totalHours;
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return { d: days, h: hours, m: minutes, s: remainingSeconds };
};

/**
 * Reduce total seconds into duration parts, respecting the allowed unit subset.
 * Excess rolls into the smallest available unit. For example, if units=['h','m']
 * and seconds=90061 (25h 1m 1s), the result is { d:0, h:25, m:1, s:0 } — the
 * remaining 1 second is dropped (floored into minutes).
 */
export const reduceDurationParts = (totalSeconds: number, units: readonly DurationUnit[]): DurationUnitParts => {
  const ordered = ALL_DURATION_UNITS.filter((u) => units.includes(u));
  const initial = Math.max(0, Math.floor(totalSeconds || 0));

  const { out } = ordered.reduce(
    (acc, u, i) => {
      const sec = UNIT_SECONDS[u];
      const isLast = i === ordered.length - 1;

      if (isLast) {
        acc.out[u] = Math.floor(acc.remaining / sec);
        acc.remaining = 0;
      } else {
        acc.out[u] = Math.floor(acc.remaining / sec);
        acc.remaining -= acc.out[u] * sec;
      }

      return acc;
    },
    { out: { d: 0, h: 0, m: 0, s: 0 }, remaining: initial }
  );

  return out;
};

/**
 * Build total seconds from duration parts. Only counts units present in the
 * units array.
 */
export const buildDurationSeconds = (parts: Partial<DurationUnitParts>, units: readonly DurationUnit[]): number => {
  return units.reduce((total, u) => total + (parts[u] || 0) * UNIT_SECONDS[u], 0);
};

/**
 * Clamp a duration value to the given min/max bounds, flooring to integer.
 */
export const clampDurationSeconds = (seconds: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, Math.max(0, Math.floor(seconds || 0))));
};
